import { orderBy } from 'lodash';

import { AGE_GROUP_APPEAL, type AgeGroupAppeal, GENDER, GENDER_TONE } from 'src/data/demographics';

import type { TargetAudienceInsights } from './types';

interface HistogramDataEntry {
  name: string;
  value: number;
}

export const getHistogramCategoryValue = (
  data: HistogramDataEntry[],
  categoryName: string
): number => {
  const category = data ? data.find((entry) => entry.name === categoryName) : null;
  return (category && category.value) || 0;
};

const GENDER_MINIMAL_DIFF = 10;
export const getGenderHistogramTone = (data: HistogramDataEntry[]) => {
  const femaleValue = getHistogramCategoryValue(data, GENDER.female);
  const maleValue = getHistogramCategoryValue(data, GENDER.male);

  const isMinimalDiff = Math.abs(femaleValue - maleValue) < GENDER_MINIMAL_DIFF;
  if (isMinimalDiff) {
    return GENDER_TONE.neutral;
  }
  return femaleValue > maleValue ? GENDER_TONE.feminine : GENDER_TONE.masculine;
};

const AGE_MINIMAL_DIFF = 10;
const calcAgeAverage = (data: HistogramDataEntry[]) => {
  const sum = (data || []).reduce((acc, entry) => acc + (entry.value || 0), 0);
  return sum / (data.length || 1);
};
export const getAgeHistogramAppeal = (data: HistogramDataEntry[]): AgeGroupAppeal => {
  const average = calcAgeAverage(data);
  const youngestAverage = calcAgeAverage(data.slice(0, 3));
  const oldestAverage = calcAgeAverage(data.slice(-3));
  const middleAverage = calcAgeAverage(data.slice(2, 5));

  if (youngestAverage - average > AGE_MINIMAL_DIFF) {
    return AGE_GROUP_APPEAL.youngest;
  }
  if (middleAverage - average > AGE_MINIMAL_DIFF) {
    return AGE_GROUP_APPEAL.middle;
  }
  if (oldestAverage - average > AGE_MINIMAL_DIFF) {
    return AGE_GROUP_APPEAL.oldest;
  }
  return AGE_GROUP_APPEAL.neutral;
};

export const AUDIENCE_CHART_ITEMS_COUNT = 5;

/**
 * Sort audiences by "isSuggested" and "score", bringing "saved" items to the top
 * @param audiences - array of audiences
 * @param options - options object
 * @param options.usedPersonaName - "name" of audience that was used for generation
 * @returns sorted audiences
 */
export const takeTopAudiences = (
  audiences?: TargetAudienceInsights[],
  options?: { usedPersonaName?: string }
) => {
  if (!audiences) {
    return [];
  }

  const usedPersonaIteratee = (item: TargetAudienceInsights) => {
    if (!options?.usedPersonaName) {
      return true;
    }
    return item?.persona?.name === options?.usedPersonaName;
  };

  // Sort by "isSuggested" and "score", bringing "saved" items to the top
  const sortedBySuggested = orderBy(
    audiences,
    [usedPersonaIteratee, 'persona.isSuggested', 'score'],
    ['desc', 'asc', 'desc']
  );

  // Take only top N items
  const onlyTopItems = sortedBySuggested.slice(0, AUDIENCE_CHART_ITEMS_COUNT);

  // Resort by "score" only
  return orderBy(onlyTopItems, ['score'], ['desc']);
};
